import { CssBaseline, ThemeProvider } from "@mui/material"
import {
  AppContextProvider,
  DialogProvider,
  QueryClientProvider,
  ROLES,
  type Role,
  SecurityDialogProvider,
  SidePanelProvider,
  ToasterProvider,
  useAppStepUpToken,
  useSso,
} from "@pharmupp/p3-front-commons"
import * as P3Themes from "@pharmupp/p3-themes"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Router } from "./Router"
import { globalStyles } from "./components/GlobalStyles"
import { secureQueryClient } from "./security"

const getThemeFromRoles = (roles: Role[] = []) => {
  switch (true) {
    case roles.includes(ROLES.ADMIN) || roles.includes(ROLES.ADHERENT_ADVISOR):
      return P3Themes.admin
    case roles.includes(ROLES.EXTERN_CONTACT_LABORATORY):
      return P3Themes.laboratory
    default:
      return P3Themes.adherent
  }
}

export function App() {
  const { sso } = useSso({
    ...window.KEYCLOAK_CONF,
    // Mock auth
    isMocked: import.meta.env.VITE_MOCKED === "true",
    ...("user-mock" in window.localStorage && {
      mockConf: {
        tokenParsed: JSON.parse(window.localStorage.getItem("user-mock")!),
      },
    }),
  })
  const { stepUpTokenService } = useAppStepUpToken()
  const roles = sso?.getRoles?.()
  const theme = getThemeFromRoles(roles)

  if (!sso) return null

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {globalStyles}
        <AppContextProvider sso={sso} stepUpToken={stepUpTokenService}>
          <QueryClientProvider client={secureQueryClient}>
            <ToasterProvider>
              <SecurityDialogProvider>
                <DialogProvider>
                  <SidePanelProvider>
                    <Router />

                    {!import.meta.env.PROD && (
                      <ReactQueryDevtools initialIsOpen={false} />
                    )}
                  </SidePanelProvider>
                </DialogProvider>
              </SecurityDialogProvider>
            </ToasterProvider>
          </QueryClientProvider>
        </AppContextProvider>
      </ThemeProvider>
    </div>
  )
}
